import type { MouseEvent, ReactElement, Ref } from "react";

import { Link, type LinkProps } from "react-router-dom";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { cx, styled } from "simplydo/react/styled";

import type { MenuItemProps } from "./Menu.types";

export type MenuItemStyleProps = { $colorValue?: string };

export const StyledMenuItem = styled.a<MenuItemStyleProps>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.4em 1em;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  color: ${({ $colorValue }) => $colorValue || "rgba(0, 0, 0, 0.87)"};
  background: transparent;
  font-weight: 400;
  opacity: 1;
  pointer-events: auto;
  user-select: none;
  box-sizing: border-box;
  margin: 0;
  border: none;
  outline: none;
  border-radius: 4px;

  &.is-active {
    background: rgba(0, 0, 0, 0.05);
    font-weight: 700;
    color: rgba(0, 0, 0, 0.95);
  }

  &.is-disabled {
    cursor: default;
    opacity: 0.45;
    pointer-events: none;
    color: rgba(40, 40, 40, 0.3);
  }

  &.is-position-right {
    margin-left: auto;
  }

  &.is-position-left {
    margin-right: auto;
  }

  &:hover {
    color: rgba(0, 0, 0, 0.95);
  }

  &:hover:not(.is-disabled):not(.is-active) {
    background: rgba(0, 0, 0, 0.03);
  }
`;

export type RenderMenuItemProps = MenuItemProps & {
  ref?: Ref<HTMLElement>;
  asLink?: boolean;
  to?: LinkProps["to"];
  href?: string;
};

export const RenderMenuItem = (props: RenderMenuItemProps): ReactElement => {
  const {
    active,
    children,
    className,
    style,
    disabled,
    position,
    onClick,
    asLink,
    to,
    href,
    ref: _ref,
    ...rest
  } = props;

  const clickable = Boolean(onClick || asLink || href || to);
  const handleClick = clickable
    ? (event: MouseEvent<HTMLElement>) => {
        if (disabled) {
          event.preventDefault();
          return;
        }
        onClick?.(event);
      }
    : undefined;

  const element = asLink ? Link : clickable ? "a" : "div";

  return (
    <StyledMenuItem
      as={element}
      className={cx(
        className,
        active && "is-active",
        disabled && "is-disabled",
        position === "right" && "is-position-right",
        position === "left" && "is-position-left",
      )}
      style={style}
      $colorValue={
        disabled
          ? "rgba(40, 40, 40, 0.3)"
          : active
            ? "rgba(0, 0, 0, 0.95)"
            : "rgba(0, 0, 0, 0.87)"
      }
      to={to}
      href={href}
      role={!disabled && onClick && !asLink ? "button" : undefined}
      tabIndex={!disabled && clickable ? 0 : undefined}
      aria-disabled={asLink && disabled ? true : undefined}
      onClick={handleClick}
      onKeyDown={handleClickForKeyboard(
        handleClick as (e: React.UIEvent) => void,
      )}
      {...(rest as object)}
    >
      {children}
    </StyledMenuItem>
  );
};

const MenuItemComponent = (props: MenuItemProps): ReactElement => (
  <RenderMenuItem {...props} />
);

export const MenuItem = styled(MenuItemComponent)``;
